/*
 * Copyright (c) CIRROM 2025.
 */

import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import React, { useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IntegratedSystem } from "../../api/integratedsystems";
import Box from "@mui/material/Box";
import ListSubheader from "@mui/material/ListSubheader";
import useMediaQuery from "@mui/material/useMediaQuery";
import { theme } from "../../theme/theme";
import { CirromLogo, CirromLogoMin } from "../Logo";
import { useSystemOrProject } from "../integrated-systems/hooks";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BallotOutlinedIcon from "@mui/icons-material/BallotOutlined";
import classes from "./Sidebar.module.css";
import IconButton from "@mui/material/IconButton";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import TextField from "@mui/material/TextField";
import { SideBarWidthContext } from "../../App";
import SyncIcon from "@mui/icons-material/Sync";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import JoinInnerIcon from "@mui/icons-material/JoinInner";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { BusinessOutlined, LocationOnOutlined } from "@mui/icons-material";
export interface CirromMonitorSidebarProps {
  onMobileClose?: any;
  openMobile?: any;
  selectedSystemId: number;
  setSelectedSystemId: (number) => void;
  userSystems: IntegratedSystem[];
}

export function CirromMonitorSidebar(
  props: Readonly<CirromMonitorSidebarProps>
) {
  const sideBarWidth = useContext(SideBarWidthContext);
  const location = useLocation();
  const { onMobileClose, openMobile } = props;
  const navigate = useNavigate();

  const selectedUserSystem = useSystemOrProject(
    props.userSystems,
    props.selectedSystemId ? props.selectedSystemId.toString() : undefined
  );

  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const selectedItemStyle = {
    backgroundColor: "#333333",
    color: "white",
    borderRadius: "5px",
  };

  const isMinimized = sideBarWidth.value === "small";

  const getNavigationLink = (path: string, additionalFilters?: string) => {
    const locationSplit = window.location.pathname.split("/");
    const datatProjectOrSystemId = locationSplit[locationSplit.length - 1];
    const filters = additionalFilters ? `?${additionalFilters}` : "";
    if (window.location.pathname.includes("data-project")) {
      return `/app/contact-monitor/data-project/${path}/${datatProjectOrSystemId}${filters}`;
    }
    return `/app/contact-monitor/${path}/${datatProjectOrSystemId}${filters}`;
  };

  const drawerContent = (
    <>
      {isMinimized ? (
        <Box
          style={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CirromLogoMin />
        </Box>
      ) : (
        <Box style={{ marginTop: "10px", marginLeft: "10px" }}>
          <CirromLogo />
        </Box>
      )}

      <Box
        style={{
          padding: "3px 10px 10px 10px",
          height: "100%",
        }}
      >
        <List style={{ paddingTop: "0px" }}>
          <Divider style={{ backgroundColor: "white" }} />
          {!isMinimized && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <ListSubheader className={classes.subHeader} disableSticky>
                CRM:
              </ListSubheader>
              <FormControl
                sx={{
                  m: 1,
                  minWidth: 120,
                  width: "100%",
                  margin: "0px",
                  padding: "0px 10px 0px 10px",
                  background: "#FCE1AC",
                  borderRadius: "8px",
                }}
                size="small"
                variant="standard"
              >
                <TextField
                  value={selectedUserSystem?.id ?? ""}
                  select
                  onChange={(event) => {
                    props.setSelectedSystemId(event.target.value);
                    navigate(`/app/dashboard/${event.target.value}`);
                  }}
                  variant={"standard"}
                  style={{ border: "none" }}
                  InputProps={{ disableUnderline: true }}
                >
                  {props.userSystems.map((userSystem) => (
                    <MenuItem
                      key={userSystem.id}
                      value={userSystem.id}
                      style={{ paddingBottom: "0px" }}
                    >
                      {userSystem.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </div>
          )}

          <Divider className={classes.divider} />
          <ListItem className={classes.subItem}>
            <Link
              to={`/app/dashboard/${selectedUserSystem?.id ?? ""}`}
              className={isMinimized ? classes.itemMin : classes.item}
              style={{
                ...(location.pathname.toLowerCase() ===
                  `/app/dashboard/${selectedUserSystem?.id ?? ""}` &&
                  selectedItemStyle),
              }}
            >
              <AssignmentOutlinedIcon fontSize={"small"} />
              {isMinimized ? (
                <Typography variant={"caption"}>All Data</Typography>
              ) : (
                <Typography variant={"h6"}>All Data</Typography>
              )}
            </Link>
          </ListItem>

          <ListItem className={classes.subItem}>
            <Link
              className={isMinimized ? classes.itemMin : classes.item}
              to={`/app/data-projects/${selectedUserSystem?.id ?? ""}`}
              style={{
                ...(location.pathname.toLowerCase() ===
                  `/app/data-projects/${selectedUserSystem?.id ?? ""}` &&
                  selectedItemStyle),
              }}
            >
              <BallotOutlinedIcon fontSize={"small"} />
              {!isMinimized && (
                <Typography variant={"h6"}>Data Projects</Typography>
              )}
              {isMinimized && (
                <Typography variant={"caption"}>Projects</Typography>
              )}
            </Link>
          </ListItem>
          <Divider className={classes.divider} />

          {selectedUserSystem && (
            <>
              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("linking", "status=suggested")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname.toLowerCase().includes("linking") &&
                      location.search
                        .toLowerCase()
                        .includes("status=suggested") &&
                      selectedItemStyle),
                  }}
                >
                  <SyncIcon fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Suggested</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Sgt'd</Typography>
                  )}
                </Link>
              </ListItem>
              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("linking", "status=matched")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname.toLowerCase().includes("linking") &&
                      location.search
                        .toLowerCase()
                        .includes("status=matched") &&
                      selectedItemStyle),
                  }}
                >
                  <JoinInnerIcon fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Matched</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Match'd</Typography>
                  )}
                </Link>
              </ListItem>
              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("linking", "status=monitored")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname.toLowerCase().includes("linking") &&
                      location.search
                        .toLowerCase()
                        .includes("status=monitored") &&
                      selectedItemStyle),
                  }}
                >
                  <MonitorHeartIcon fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Monitored</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Monitor</Typography>
                  )}
                </Link>
              </ListItem>

              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("name-changes")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname
                      .toLowerCase()
                      .includes("name-changes") && selectedItemStyle),
                  }}
                >
                  <BadgeOutlinedIcon fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Name Changes</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Name</Typography>
                  )}
                </Link>
              </ListItem>
              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("employment-changes")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname
                      .toLowerCase()
                      .includes("employment-changes") && selectedItemStyle),
                  }}
                >
                  <BusinessOutlined fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Employment Changes</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Employ.</Typography>
                  )}
                </Link>
              </ListItem>
              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("location-changes")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname
                      .toLowerCase()
                      .includes("location-changes") && selectedItemStyle),
                  }}
                >
                  <LocationOnOutlined fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Location Changes</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Loc.</Typography>
                  )}
                </Link>
              </ListItem>
              <ListItem className={classes.subItem}>
                <Link
                  to={getNavigationLink("alerts")}
                  className={isMinimized ? classes.itemMin : classes.item}
                  style={{
                    ...(location.pathname.toLowerCase().includes("alerts") &&
                      selectedItemStyle),
                  }}
                >
                  <NotificationsActiveIcon fontSize={"small"} />
                  {!isMinimized && (
                    <Typography variant={"h6"}>Alerts</Typography>
                  )}
                  {isMinimized && (
                    <Typography variant={"caption"}>Alerts</Typography>
                  )}
                </Link>
              </ListItem>
            </>
          )}
        </List>
      </Box>

      <div
        style={{
          display: "flex",
          height: "100%",
          flexDirection: "column-reverse",
          alignItems: "end",
        }}
      >
        <IconButton
          onClick={() => sideBarWidth.toggleValue()}
          style={{ color: "#333333" }}
          disableRipple
        >
          <KeyboardDoubleArrowLeftIcon fontSize={"small"} />
        </IconButton>
      </div>
    </>
  );

  return (
    <>
      <Drawer
        anchor="left"
        variant="temporary"
        open={openMobile}
        onClose={onMobileClose}
        PaperProps={{
          style: { width: 240, height: "100%" },
        }}
        style={{ display: !lgUp ? "inherit" : "none" }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        anchor="left"
        variant="persistent"
        open={sideBarWidth.value === "full"}
        PaperProps={{
          style: { width: 240, height: "100%" },
        }}
        style={{ display: lgUp ? "inherit" : "none" }}
      >
        {drawerContent}
      </Drawer>

      {isMinimized && (
        <Drawer
          anchor="left"
          variant="persistent"
          open={sideBarWidth.value === "small"}
          PaperProps={{
            style: { width: 52, height: "100%" },
          }}
          style={{ display: lgUp ? "inherit" : "none" }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}
